import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getFilteredFinSobRows } from '../../lib'
import { isActualsFinSobRow } from '../../lib/revenueAndSob/isActualsFinSobRow'
import {
  selectCockpitCategory,
  selectCockpitOffering,
  selectCockpitPeriods,
  selectVisibleCockpitFinSobRows,
} from '../selectors'
import { IEnrichedCockpitFinSobRow } from '../types'

import { useEffectiveActualsSync } from './useEffectiveActualsSync'

export interface IUseFilteredFinSobRowsResult {
  totalRows: IEnrichedCockpitFinSobRow[]
  targetRows: IEnrichedCockpitFinSobRow[]
  actualsRows: IEnrichedCockpitFinSobRow[]
  quotaRows: IEnrichedCockpitFinSobRow[]
}

/** Fin/SOB rows scoped to the dashboard's selected offering and period, split by classification.
 *  Quarter `5` (the full-year total) is excluded — only per-quarter rows (`1`-`4`) come back. */
export const useFilteredFinSobRows = (): IUseFilteredFinSobRowsResult => {
  const rows = useSelector(selectVisibleCockpitFinSobRows)

  const offering = useSelector(selectCockpitOffering)
  const periods = useSelector(selectCockpitPeriods)
  const { currentFQuarter } = useEffectiveActualsSync()
  const selectedCategory = useSelector(selectCockpitCategory)

  const scopedRows = useMemo(
    () =>
      getFilteredFinSobRows({
        currentFQuarter,
        rows,
        offering,
        periods,
        category: selectedCategory,
      }),
    [rows, offering, periods, currentFQuarter, selectedCategory]
  )

  const targetRows = useMemo(() => scopedRows.filter((row) => row.isTarget), [scopedRows])
  const actualsRows = useMemo(() => scopedRows.filter(isActualsFinSobRow), [scopedRows])
  const quotaRows = useMemo(() => scopedRows.filter((row) => row.isQuota), [scopedRows])

  return {
    totalRows: scopedRows,
    targetRows,
    actualsRows,
    quotaRows,
  }
}
